import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CircularProgress, Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../Buttons";
import {
  CustomCheckbox,
  CustomInputField,
  CustomRadioGroup,
} from "../FormInputs";
import CaseService from "../../../services/CaseService";
import _ from "lodash";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { encrypt, renderFeeKind } from "../../../helpers/functions";
import { PAYMENT_STATUS } from "../../../helpers/constants";
import { refundScheme } from "../../../helpers/validationSchema";
import { FieldArray, Form, Formik } from "formik";
import NumberFormat from "react-number-format";
import { Fragment } from "react";

export default function RefundModal({
  openRefundModal,
  setOpenRefundModal,
  id = "",
  heading = "",
  btnText = "",
  viewOnly,
}) {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function getPaymentHistory(id) {
      try {
        setLoading(true);
        const response = await CaseService.getPayments(id);
        if (response?.data) {
          setPaymentHistory(response?.data);
        }
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    }

    if (id && openRefundModal) {
      getPaymentHistory(id);
    }
  }, [id, openRefundModal]);

  const submitForRefund = async (payload) => {
    try {
      setLoader({ state: true, message: `Submitting for refund ...` });
      const res = await CaseService.refundPayment(payload);
      if (res?.message) {
        enqueueSnackbar(res?.message, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Unable to process Refund", { variant: "error" });
    } finally {
      setLoader({ state: false });
      setOpenRefundModal(false);
    }
  };

  const onFormSubmit = (values) => {
    const payload = {};
    payload.refundComment = values?.comment;
    payload.refunds = values?.payments
      ?.filter((payment) => payment.checked)
      .map((el) => {
        return {
          id: el?.id,
          value:
            el?.type === "full"
              ? encrypt(
                  Number(
                    el?.cartItem?.fee?.discPrice
                      ?.toString()
                      ?.split(",")
                      ?.join("")
                  )
                )
              : encrypt(Number(el?.amount?.toString()?.split(",")?.join(""))),
        };
      });
    submitForRefund(payload);
  };

  return (
    <Modal
      open={openRefundModal}
      onClose={() => setOpenRefundModal(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <ModalContent>
          <HeadingContainer>
            <HeaderLeft>
              <HeaderText>
                <Heading>{heading}</Heading>
                <Subheading>Review and process payment refunds</Subheading>
              </HeaderText>
            </HeaderLeft>
            <CloseButton
              onClick={() => setOpenRefundModal(false)}
              aria-label="Close modal"
            >
              <img
                src={require("../../../assets/images/closeModal.svg")}
                alt="close"
              />
            </CloseButton>
          </HeadingContainer>

          <Divider />

          {loading ? (
            <LoadingContainer>
              <CircularProgress size={40} style={{ color: "#00838c" }} />
              <LoadingText>Loading payment history...</LoadingText>
            </LoadingContainer>
          ) : (
            <Formik
              initialValues={{
                payments: paymentHistory?.map((el) => {
                  return {
                    ...el,
                    amount: el?.cartItem?.fee?.discPrice,
                    type: "full",
                    checked: false,
                  };
                }),
                comment: "",
              }}
              validationSchema={refundScheme}
              enableReinitialize={false}
              onSubmit={onFormSubmit}
              validateOnBlur
              validateOnChange
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldTouched,
                isValid,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <ContentWrapper>
                    <NoteContainer>
                      <SectionHeader>
                        <SectionTitle>Payment History</SectionTitle>
                        {values?.payments?.length > 0 && (
                          <PaymentCount>
                            {values.payments.length} payment
                            {values.payments.length !== 1 ? "s" : ""}
                          </PaymentCount>
                        )}
                      </SectionHeader>

                      <PaymentsList>
                        <FieldArray
                          name="payments"
                          render={() => (
                            <Fragment>
                              {values?.payments?.map((payment, index) => (
                                <Fragment key={index}>
                                  <PaymentCard
                                    isRefunded={!!payment?.refundedAt}
                                    isDisabled={
                                      !Number(
                                        payment?.cartItem?.fee?.discPrice
                                      ) ||
                                      !Number(
                                        payment?.allowedRefund?.totalRefund
                                      )
                                    }
                                  >
                                    <PaymentCardHeader>
                                      <PaymentInfo>
                                        <FeeLabel>
                                          {renderFeeKind(
                                            PAYMENT_STATUS[
                                              payment?.cartItem?.feeKind
                                            ],
                                            payment?.case
                                          )}{" "}
                                          Fee
                                        </FeeLabel>
                                        <AgentRole>
                                          {_.startCase(
                                            payment?.cartItem?.agentRole
                                          )}
                                        </AgentRole>
                                      </PaymentInfo>
                                      <PaymentAmount>
                                        <AmountValue>
                                          {payment?.cartItem?.fee?.unit}{" "}
                                          {Number(
                                            payment?.cartItem?.fee?.discPrice
                                          ).toFixed(2)}
                                        </AmountValue>
                                      </PaymentAmount>
                                    </PaymentCardHeader>

                                    <PaymentCardFooter>
                                      {payment?.refundedAt ? (
                                        <RefundedBadge>
                                          <CheckIcon>✓</CheckIcon>
                                          <div>
                                            <RefundedText>
                                              Refunded
                                            </RefundedText>
                                            <RefundedAmount>
                                              {payment?.cartItem?.fee?.unit}{" "}
                                              {Number(
                                                payment?.refundAmount
                                              ).toFixed(2)}
                                            </RefundedAmount>
                                          </div>
                                        </RefundedBadge>
                                      ) : (
                                        <CheckboxWrapper
                                          disabled={
                                            !Number(
                                              payment?.cartItem?.fee?.discPrice
                                            ) ||
                                            !Number(
                                              payment?.allowedRefund
                                                ?.totalRefund
                                            )
                                          }
                                        >
                                          <CustomCheckbox
                                            id={`refund-${index}`}
                                            name={`payments[${index}].checked`}
                                            checked={
                                              values?.payments[index]?.checked
                                            }
                                            disabled={
                                              !Number(
                                                payment?.cartItem?.fee
                                                  ?.discPrice
                                              ) ||
                                              !Number(
                                                payment?.allowedRefund
                                                  ?.totalRefund
                                              )
                                            }
                                            onChange={(e) => {
                                              setFieldValue(
                                                `payments[${index}].type`,
                                                "full"
                                              );
                                              handleChange(e);
                                            }}
                                          />
                                          <CheckboxLabel>
                                            Select for refund
                                          </CheckboxLabel>
                                        </CheckboxWrapper>
                                      )}
                                    </PaymentCardFooter>

                                    {payment.checked && (
                                      <RefundOptionsContainer>
                                        <RefundOptionsHeader>
                                          Refund Options
                                        </RefundOptionsHeader>
                                        <RadioGroupWrapper>
                                          <CustomRadioGroup
                                            radioInputs={[
                                              {
                                                label: "Full Refund",
                                                value: "full",
                                              },
                                              {
                                                label: "Partial Refund",
                                                value: "partial",
                                              },
                                            ]}
                                            value={values.payments[index].type}
                                            name={`payments[${index}].type`}
                                            {...{ values, handleChange }}
                                          />
                                        </RadioGroupWrapper>
                                        <AmountInputWrapper>
                                          {values?.payments[index]?.type ===
                                          "partial" ? (
                                            <InputContainer>
                                              <InputLabel>
                                                Refund Amount
                                              </InputLabel>
                                              <NumberFormat
                                                thousandSeparator
                                                thousandsGroupStyle="lakh"
                                                isNumericString={true}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                className={"input-white"}
                                                displayType={"input"}
                                                value={
                                                  values.payments[index].amount
                                                }
                                                type={"text"}
                                                customInput={CustomInputField}
                                                name={`payments[${index}].amount`}
                                                onChange={handleChange}
                                                variant="outlined"
                                                error={
                                                  errors?.payments?.[index]
                                                    ?.amount &&
                                                  touched?.payments?.[index]
                                                    ?.amount
                                                }
                                                onBlur={(e) => {
                                                  setFieldTouched(
                                                    `payments[${index}].amount`,
                                                    true
                                                  );
                                                }}
                                              />
                                              {errors?.payments?.[index]
                                                ?.amount &&
                                                touched?.payments?.[index]
                                                  ?.amount && (
                                                  <HelperText>
                                                    {
                                                      errors?.payments?.[index]
                                                        ?.amount
                                                    }
                                                  </HelperText>
                                                )}
                                            </InputContainer>
                                          ) : (
                                            <FullRefundDisplay>
                                              <InputLabel>
                                                Refund Amount
                                              </InputLabel>
                                              <FullRefundValue>
                                                {payment?.cartItem?.fee?.unit}{" "}
                                                {Number(
                                                  payment?.cartItem?.fee
                                                    ?.discPrice
                                                ).toFixed(2)}
                                              </FullRefundValue>
                                            </FullRefundDisplay>
                                          )}
                                        </AmountInputWrapper>
                                      </RefundOptionsContainer>
                                    )}
                                  </PaymentCard>
                                </Fragment>
                              ))}
                            </Fragment>
                          )}
                        />
                      </PaymentsList>

                      {values?.payments?.every(
                        (el) => el.refundedAt !== null
                      ) ? null : (
                        <CommentsSection>
                          <CommentHeader>
                            <CommentLabel>Add Comments (Optional)</CommentLabel>
                          </CommentHeader>
                          <CustomInputField
                            variant="outlined"
                            className={"input-height-auto"}
                            multiline={true}
                            rows={4}
                            maxRows={4}
                            placeholder="Add any additional notes or reasons for the refund..."
                            name={"comment"}
                            value={values?.comment}
                            onChange={handleChange}
                          />
                        </CommentsSection>
                      )}
                    </NoteContainer>

                    <Divider />

                    <ButtonContainer>
                      <PrimaryOutlinedCloseCTAButton
                        onClick={() => setOpenRefundModal(false)}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      {viewOnly !== true && (
                        <PrimaryCTAButton
                          type={"submit"}
                          disabled={
                            !isValid ||
                            !values?.payments.some((el) => el.checked)
                          }
                        >
                          {btnText || "Process Refund"}
                        </PrimaryCTAButton>
                      )}
                    </ButtonContainer>
                  </ContentWrapper>
                </Form>
              )}
            </Formik>
          )}
        </ModalContent>
      </DrawerContainer>
    </Modal>
  );
}

const DrawerContainer = styled.div`
  max-width: 95%;
  width: 100%;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media ${theme?.breakpoints?.sm_up} {
    max-width: 600px;
  }
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeadingContainer = styled.div`
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  line-height: 1.2;
`;

const Subheading = styled.p`
  margin: 0;
  padding: 0;
  font-size: 13px;
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, #e0e0e0, transparent);
  margin: 0 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const LoadingText = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #666;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(90vh - 100px);
`;

const NoteContainer = styled.div`
  padding: 24px 28px;
  overflow-y: auto;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  flex: 1;
`;

const PaymentCount = styled.span`
  background: #e8f5f6;
  color: #00838c;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const PaymentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentCard = styled.div`
  background: ${(props) =>
    props.isRefunded ? "#f0f9ff" : props.isDisabled ? "#f5f5f5" : "#ffffff"};
  border: 2px solid
    ${(props) =>
      props.isRefunded ? "#00838c" : props.isDisabled ? "#e0e0e0" : "#e8e8e8"};
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};

  &:hover {
    ${(props) =>
      !props.isDisabled &&
      `
      box-shadow: 0 2px 12px rgba(0, 131, 140, 0.1);
    `}
  }
`;

const PaymentCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const FeeLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 4px;
`;

const AgentRole = styled.div`
  font-size: 13px;
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFont};
`;

const PaymentAmount = styled.div`
  text-align: right;
`;

const AmountValue = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const PaymentCardFooter = styled.div`
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

const RefundedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #e8f5f6;
  padding: 12px 16px;
  border-radius: 8px;
`;

const CheckIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #00838c;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
`;

const RefundedText = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  font-weight: 700;
  color: #00838c;
`;

const RefundedAmount = styled.div`
  font-size: 12px;
  color: #666;
  font-family: ${theme.fonts.primaryFont};
  font-style: italic;
`;

const RefundOptionsContainer = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px dashed #e0e0e0;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
`;

const RefundOptionsHeader = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 12px;
`;

const RadioGroupWrapper = styled.div`
  margin-bottom: 16px;
`;

const AmountInputWrapper = styled.div`
  margin-top: 12px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const InputLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.INPUT_LABEL};
  margin-bottom: 8px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const FullRefundDisplay = styled.div``;

const FullRefundValue = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  padding: 12px 16px;
  background: #ffffff;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
`;

const HelperText = styled.div`
  position: absolute;
  left: 3px;
  top: 100%;
  margin-top: 4px;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #f44336;
`;

const CommentsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const CommentLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 28px;
  background: #fafafa;

  button {
    min-width: 140px;
    padding: 12px 24px !important;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;
